import { ReactNode, useEffect, useRef, useId, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { createDebug } from '@ponpon/api-client'

const log = createDebug('dw:ui:modal')

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
}

export default function Modal({ isOpen, onClose, children, title, size = 'lg' }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<Element | null>(null)
  const titleId = useId()
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const handleClose = useCallback(() => {
    onCloseRef.current()
  }, [])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialogRef.current) return

      const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault()
        lastElement?.focus()
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault()
        firstElement?.focus()
      }
    }

    if (isOpen) {
      previousActiveElement.current = document.activeElement
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('keydown', handleTab)
      document.body.style.overflow = 'hidden'
      log('open title=%s size=%s mounted=%s', title ?? '(untitled)', size, 'portal:document.body')
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('keydown', handleTab)
      document.body.style.overflow = ''
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus()
      }
    }
  }, [isOpen, handleClose, title, size])

  if (!isOpen) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/70 overflow-y-auto animate-fade-in"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="presentation"
      data-testid="modal-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={cn(
          'relative w-full bg-surface-1 rounded-lg border border-border overflow-hidden animate-scale-in',
          sizeClasses[size]
        )}
      >
        <button
          onClick={handleClose}
          className={cn(
            'absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-md',
            'bg-transparent hover:bg-surface-2 transition-colors',
            'text-muted-foreground hover:text-foreground cursor-pointer'
          )}
          aria-label="Close dialog"
        >
          <X className="w-4 h-4" />
        </button>

        {title && (
          <div className="px-5 py-4 border-b border-border">
            <h2 id={titleId} className="text-sm font-medium text-foreground">{title}</h2>
          </div>
        )}

        <div className="p-5 max-h-[85vh] overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  )
}
